import React, { useState } from 'react';
import { AreaChart, Area, ResponsiveContainer, XAxis } from 'recharts';
import { Check, Copy } from 'lucide-react';
import { Container, SectionHeader, Reveal } from './landing/primitives';

const mockData = [
  { t: '00:00', v: 120 }, { t: '04:00', v: 280 }, { t: '08:00', v: 650 },
  { t: '12:00', v: 890 }, { t: '16:00', v: 1200 }, { t: '20:00', v: 950 }, { t: '24:00', v: 1100 },
];

const SNIPPETS = {
  python: `import opensov

client = opensov.Client(api_key="sk-...")

response = client.chat.completions.create(
    model="opensov-1",
    messages=[{"role": "user", "content": "Explain quantum computing"}],
    stream=True,
)

for chunk in response:
    print(chunk.choices[0].delta.content)`,
  curl: `curl https://api.opensov.com/v1/chat/completions \\
  -H "Authorization: Bearer sk-..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "opensov-1",
    "messages": [{"role": "user", "content": "Explain quantum computing"}],
    "stream": true
  }'`,
  node: `import OpenSov from "opensov";

const client = new OpenSov({ apiKey: "sk-..." });

const stream = await client.chat.completions.create({
  model: "opensov-1",
  messages: [{ role: "user", content: "Explain quantum computing" }],
  stream: true,
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0].delta.content ?? "");
}`,
};

const CodePanel = () => {
  const [lang, setLang] = useState('python');
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(SNIPPETS[lang]);
      } else {
        const ta = document.createElement('textarea');
        ta.value = SNIPPETS[lang];
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (e) { /* noop */ }
        document.body.removeChild(ta);
      }
    } catch (e) {
      // Clipboard permission denied in restricted contexts (e.g. embedded preview) - still show feedback
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="border border-line bg-surface rounded-xl overflow-hidden flex flex-col" data-testid="showcase-code">
      <div className="flex items-center justify-between px-4 border-b border-line">
        <div className="flex">
          {Object.keys(SNIPPETS).map((k) => (
            <button
              key={k}
              onClick={() => setLang(k)}
              className={`px-3 py-3 text-[12px] font-mono capitalize border-b transition-colors duration-200 ${lang === k ? 'text-content border-content' : 'text-content-3 border-transparent hover:text-content'}`}
              data-testid={`code-tab-${k}`}
            >
              {k}
            </button>
          ))}
        </div>
        <button onClick={copy} className="inline-flex items-center gap-1.5 text-[11px] font-mono text-content-3 hover:text-content transition-colors" data-testid="code-copy-btn">
          {copied ? <Check className="w-3.5 h-3.5 text-[#10B981]" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="p-6 font-mono text-[12.5px] leading-6 text-content-2 overflow-x-auto flex-1">{SNIPPETS[lang]}</pre>
    </div>
  );
};

const ThroughputPanel = () => (
  <div className="border border-line bg-surface rounded-xl overflow-hidden flex flex-col" data-testid="showcase-dashboard">
    <div className="px-6 pt-6 flex items-start justify-between">
      <div>
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-content-3">API throughput</p>
        <p className="mt-2 text-3xl font-heading font-medium tracking-tight text-content tabular-nums">
          1,247 <span className="text-sm text-content-3 font-sans">req/s</span>
        </p>
      </div>
      <span className="text-[11px] font-mono text-[#10B981]">+23.5%</span>
    </div>
    <div className="h-44 mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={mockData} margin={{ left: 0, right: 0 }}>
          <defs>
            <linearGradient id="showcaseGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" axisLine={false} tickLine={false} tick={{ fill: 'var(--c-content-3)', fontSize: 10, fontFamily: 'JetBrains Mono' }} />
          <Area type="monotone" dataKey="v" stroke="#10B981" strokeWidth={1.5} fill="url(#showcaseGrad)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
    <div className="grid grid-cols-3 border-t border-line">
      {[['Avg latency', '42ms'], ['Uptime', '99.99%'], ['Active keys', '2,847']].map(([l, v], i) => (
        <div key={l} className={`px-6 py-4 ${i > 0 ? 'border-l border-line' : ''}`}>
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-content-3">{l}</p>
          <p className="mt-1 text-base font-heading font-medium text-content tabular-nums">{v}</p>
        </div>
      ))}
    </div>
  </div>
);

const ProductShowcase = () => (
  <section className="py-24 lg:py-32 border-t border-line" id="api-platform" data-testid="showcase-section">
    <Container>
      <SectionHeader
        eyebrow="Developers"
        title="Benchmark speed. Familiar ergonomics."
        subtitle="OpenAI-compatible endpoints, real-time streaming and SDKs in every language. Ship in an afternoon."
      />
      <Reveal delay={0.1} className="mt-16 lg:mt-20 grid lg:grid-cols-2 gap-6">
        <ThroughputPanel />
        <CodePanel />
      </Reveal>
    </Container>
  </section>
);

export default ProductShowcase;
